import React from 'react';
import { fmtDateTime } from '../../utils/formatters';
import { TONE_TEXT } from '../../utils/statusColors';
import { CabecalhoInterno } from '../shared/lista/PecasDaLista';

const fmt = (v: any) => {
  const n = parseFloat(String(v));
  return isNaN(n) ? '0,00' : n.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const corMovimento = (cod: string) =>
  cod === '06' || cod === '17' ? TONE_TEXT.success :
  cod === '03' || cod === '26' || cod === '30' ? 'text-red-400' :
  cod === '44' ? 'text-amber-400' :
  cod === '02' ? 'text-cyan-400' :
  ['05', '09', '25', '28'].includes(cod) ? 'text-gray-400' :
  'text-slate-400';

const RegistroMovimento: React.FC<{ d: any }> = ({ d }) => (
  <div className="flex items-center gap-3 p-1.5 bg-slate-700/50 rounded">
    <span className="text-slate-400 font-mono text-[11px] w-24 shrink-0">{d.nossoNumero}</span>
    <span className="text-white font-medium w-20 shrink-0">{d.invoiceCode || '-'}</span>
    {d.sacadoNome && <span className="text-slate-300 truncate max-w-[180px]" title={d.sacadoNome}>{d.sacadoNome}</span>}
    <span className={corMovimento(d.codMovimento)}>{d.codMovimento} - {d.descricaoMovimento}</span>
    {d.matchMethod && d.matchMethod !== 'nossoNumero' && (
      <span className="px-1.5 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px]">
        {d.matchMethod === 'cnpj+valor' ? 'CNPJ' : 'CNPJ+Venc'}
      </span>
    )}
    {d.valorPago != null && <span className="text-central-texto ml-auto font-mono">R$ {fmt(d.valorPago)}</span>}
    {d.motivoRejeicao && <span className="text-red-300 text-xs">{d.motivoRejeicao}</span>}
  </div>
);

export const RetornoHistoryPanel: React.FC<{
  loading: boolean;
  /** C5: não null = a leitura do histórico falhou (antes virava "Nenhum retorno processado"). */
  erro?: string | null;
  onTentarDeNovo?: () => void;
  history: any[];
  expandedId: number | null;
  onToggle: (id: number) => void;
  onClose: () => void;
}> = ({ loading, erro, onTentarDeNovo, history, expandedId, onToggle, onClose }) => (
  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
    <div className="flex items-center justify-between mb-3">
      <h3 className="text-white font-semibold">Historico de Retornos</h3>
      <button onClick={onClose} className="text-slate-400 hover:text-slate-300 text-sm">Fechar</button>
    </div>
    {loading ? (
      <p className="text-slate-400 text-sm py-4 text-center">Carregando...</p>
    ) : erro ? (
      <p role="alert" className="text-sm py-4 text-center text-slate-300" data-historico-nao-lido>
        Não consegui ler o histórico de retornos ({erro}).{' '}
        {onTentarDeNovo && <button type="button" onClick={onTentarDeNovo} className="underline">Tentar de novo</button>}
      </p>
    ) : history.length === 0 ? (
      <p className="text-slate-500 text-sm py-4 text-center">Nenhum retorno processado</p>
    ) : (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <CabecalhoInterno>
            <tr className="border-b border-slate-700">
              <th className="p-2 text-left text-slate-400 font-medium">Data</th>
              <th className="p-2 text-left text-slate-400 font-medium">Arquivo</th>
              <th className="p-2 text-right text-slate-400 font-medium">Processados</th>
              <th className="p-2 text-right text-slate-400 font-medium">Pagos</th>
              <th className="p-2 text-right text-slate-400 font-medium">Confirmados</th>
              <th className="p-2 text-right text-slate-400 font-medium">Rejeitados</th>
              <th className="p-2 text-right text-slate-400 font-medium">Erros</th>
              <th className="p-2 text-left text-slate-400 font-medium">Usuario</th>
            </tr>
          </CabecalhoInterno>
          <tbody>
            {history.map((h: any) => {
              const errors = h.details?.errors || [];
              const isExpanded = expandedId === h.id;
              return (
                <React.Fragment key={h.id}>
                  <tr className="border-b border-slate-700/50 hover:bg-slate-700/30 cursor-pointer" onClick={() => onToggle(h.id)}>
                    <td className="p-2 text-slate-300 text-xs">
                      <span className="text-slate-500 mr-1">{isExpanded ? '▼' : '▶'}</span>
                      {fmtDateTime(h.importedAt)}
                    </td>
                    <td className="p-2 text-white text-xs font-mono max-w-[160px] truncate" title={h.filename}>{h.filename}</td>
                    <td className="p-2 text-right text-white">{h.recordsProcessed}</td>
                    <td className="p-2 text-right text-central-texto">{h.details?.paid || 0}</td>
                    <td className="p-2 text-right text-cyan-400">{h.details?.confirmed || 0}</td>
                    <td className="p-2 text-right text-red-400">{h.details?.rejected || 0}</td>
                    <td className="p-2 text-right text-red-300">{errors.length}</td>
                    <td className="p-2 text-slate-400 text-xs">{h.userName || '-'}</td>
                  </tr>
                  {isExpanded && (
                    <tr>
                      <td colSpan={8} className="p-0">
                        <div className="bg-slate-900/50 p-3 text-xs space-y-2">
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 text-slate-300 mb-2">
                            <div>Baixados: <span className="text-gray-400 font-medium">{h.details?.writtenOff || 0}</span></div>
                            <div>Importados: <span className="text-white font-medium">{h.recordsImported}</span></div>
                            <div>Duplicatas: <span className="text-slate-400 font-medium">{h.duplicates || 0}</span></div>
                          </div>
                          {errors.length > 0 && (
                            <div className="p-2 bg-red-500/10 rounded-lg mb-2">
                              <p className="text-red-400 font-medium mb-1">Erros:</p>
                              {errors.map((err: string, i: number) => <p key={i} className="text-red-300">{err}</p>)}
                            </div>
                          )}
                          {h.details?.records?.length > 0 ? (
                            <div className="max-h-60 overflow-y-auto space-y-1">
                              {h.details.records.map((d: any, i: number) => <RegistroMovimento key={i} d={d} />)}
                            </div>
                          ) : (
                            <p className="text-slate-500">Detalhes individuais nao disponiveis para este retorno.</p>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

export const RetornoResultPanel: React.FC<{ result: any; onClose: () => void }> = ({ result, onClose }) => (
  <div className="bg-slate-800 rounded-xl p-4 border border-slate-700">
    <h3 className="text-white font-semibold mb-3">Resultado do Retorno</h3>
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 text-sm mb-3">
      <div><span className="text-slate-400">Processados: </span><span className="text-white font-medium">{result.processed || 0}</span></div>
      <div><span className="text-slate-400">Pagos: </span><span className="text-central-texto font-medium">{result.paid || 0}</span></div>
      <div><span className="text-slate-400">Confirmados: </span><span className="text-cyan-400 font-medium">{result.confirmed || 0}</span></div>
      <div><span className="text-slate-400">Rejeitados: </span><span className="text-red-400 font-medium">{result.rejected || 0}</span></div>
      <div><span className="text-slate-400">Baixados: </span><span className="text-gray-400 font-medium">{result.writtenOff || 0}</span></div>
    </div>
    {result.errors?.length > 0 && (
      <div className="mb-3 p-3 bg-red-500/10 rounded-lg">
        <p className="text-red-400 text-xs font-medium mb-1">Erros:</p>
        {result.errors.map((err: string, i: number) => <p key={i} className="text-red-300 text-xs">{err}</p>)}
      </div>
    )}
    {result.details?.length > 0 && (
      <details className="text-xs">
        <summary className="text-slate-400 cursor-pointer hover:text-slate-300 mb-2">
          Ver detalhes ({result.details.length} registros)
        </summary>
        <div className="max-h-80 overflow-y-auto space-y-1">
          {result.details.map((d: any, i: number) => <RegistroMovimento key={i} d={d} />)}
        </div>
      </details>
    )}
    <button onClick={onClose} className="mt-3 text-sm text-slate-400 hover:text-slate-300">Fechar</button>
  </div>
);
